// @file Pinia store to manage special effects for whiteboard
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { sfxManager } from '@@/bits/sfx'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { vfxManager } from '@@/bits/vfx'
import { unboundedWatchEffect } from '@@/bits/vue'
import { deleteShapeMetadata, getShapeOriginInViewport, isGroupShape, isShapeChildOfGroup } from '@@/bits/whiteboard'
import { updateSpecialEffectsInAllShapesInGroup } from '@@/bits/whiteboard_special_effects'
import { SPECIAL_EFFECTS_TOOL_ID } from '@@/bits/whiteboard_tools'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useWhiteboardAttachmentsStore } from '@@/pinia/whiteboard_attachments'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { WhiteboardSoundEffectsApi } from '@@/surface/api/whiteboard_sound_effects'
import type { WhiteboardSoundEffect } from '@@/types'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { LinkHelpers } from '@padlet/beethoven-client'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { type JsonObject, type TLShape, type TLShapeId } from 'tldraw'
import { computed, readonly, ref, shallowRef, watch } from 'vue'

const SUBCATEGORY_NONE = Symbol('none')

interface BaseEffectItem {
  id: string
  title: string
  trackingId?: string
}

type SoundEffectItem = BaseEffectItem & {
  category: 'sounds'
  subcategory: string | symbol
  thumbnailUrl: string
  audioUrl: string
  searchKeywords: string[]
}

type VisualEffectItem = BaseEffectItem & {
  category: 'visuals'
  subcategory?: undefined
  iconName: string
}

interface ShapeSpecialEffects {
  visual_effect?: { effect_id: string }
  sound_effect?: { type: 'system'; effect_id: string }
}

interface SpecialEffectCategory {
  id: 'sounds' | 'visuals'
  title: string
}

const CATEGORY_ID_TO_SHAPE_SPECIAL_EFFECT_KEY_MAP: Record<SpecialEffectCategory['id'], keyof ShapeSpecialEffects> = {
  visuals: 'visual_effect',
  sounds: 'sound_effect',
}

interface EffectItemColor {
  fg: string
  bg: string
}

interface SoundEffectItemWithColor extends SoundEffectItem {
  color: EffectItemColor
}

type SpecialEffectItem = SoundEffectItemWithColor | VisualEffectItem

// https://www.figma.com/design/WOsb7OKvuBXgJ9QZ6Jpnbp/Special-Effects?node-id=906-33521&m=dev
const SOUND_EFFECT_ITEM_COLORS: EffectItemColor[] = [
  {
    fg: '#A6A1FF',
    bg: '#F0F0FF',
  },
  {
    fg: '#5BB3FE',
    bg: '#E5F3FF',
  },
  {
    fg: '#14b8a6',
    bg: '#DBFAF7',
  },
  {
    fg: '#3DC731',
    bg: '#E2F7DE',
  },
  {
    fg: '#ABD30C',
    bg: '#EEF8BF',
  },
  {
    fg: '#FEC006',
    bg: '#FCF2CF',
  },
  {
    fg: '#FFAF65',
    bg: '#FFEFE0',
  },
  {
    fg: '#FF8B66',
    bg: '#FFECE5',
  },
  {
    fg: '#CDA681',
    bg: '#EDE8E3',
  },
]

export const useWhiteboardSpecialEffectsStore = defineStore('whiteboardSpecialEffects', () => {
  // #region Visual effects
  // Remember to update services/morpheus/scripts/whiteboard_ai/special_effects/index.py with new visual effects and reindex in Elasticsearch
  const visualEffects: VisualEffectItem[] = [
    {
      id: 'confetti',
      title: __('Confetti'),
      iconName: 'confetti',
    },
    {
      id: 'fireworks',
      title: __('Fireworks'),
      iconName: 'fireworks',
    },
    {
      id: 'bubbles',
      title: __('Bubbles'),
      iconName: 'bubbles',
    },
    {
      id: 'hearts',
      title: __('Hearts'),
      iconName: 'hearts',
    },
    {
      id: 'balloons',
      title: __('Balloons'),
      iconName: 'balloons',
    },
    {
      id: 'snowfall',
      title: __('Snowfall'),
      iconName: 'snowflakes',
    },
  ].map((i) => ({ ...i, category: 'visuals' }))

  const allVisualEffectIds = visualEffects.map((effect) => effect.id)
  const loadedVisualEffectIds = new Set<string>()

  const getAllVisualEffectIdsOnWhiteboard = (): Array<VisualEffectItem['id']> => {
    const editor = getEditorObject()
    if (editor == null) return []
    return editor
      .getPages()
      .flatMap((page) => Array.from(editor.getPageShapeIds(page)))
      .map((id) => {
        const { visual_effect: visualEffect } = getSpecialEffectsOfShape(id)
        return visualEffect?.effect_id
      })
      .filter(Boolean) as Array<VisualEffectItem['id']>
  }

  /**
   * Preloads visual effects if there are shapes using them on the whiteboard
   */
  const preloadActiveVisualEffects = async (): Promise<void> => {
    const ids = getAllVisualEffectIdsOnWhiteboard()
    const newIds = ids.filter((id) => !loadedVisualEffectIds.has(id))
    await vfxManager.preloadEffects(newIds)
    newIds.forEach((id) => loadedVisualEffectIds.add(id))
  }
  // #endregion

  // #region Sound effects
  const getAllSoundEffectIdsOnWhiteboard = (): Array<SoundEffectItem['id']> => {
    const editor = getEditorObject()
    if (editor == null) return []
    return editor
      .getPages()
      .flatMap((page) => Array.from(editor.getPageShapeIds(page)))
      .map((id) => {
        const { sound_effect: soundEffect } = getSpecialEffectsOfShape(id)
        return soundEffect?.effect_id
      })
      .filter(Boolean) as Array<SoundEffectItem['id']>
  }

  const soundEffects = shallowRef<SoundEffectItem[]>([])
  const soundEffectsFetchAllState = ref<'idle' | 'fetching' | 'success' | 'error'>('idle')
  const soundEffectsFetchSubsetAbortController = ref<AbortController | null>(null)

  const soundEffectsWithColor = computed<SoundEffectItemWithColor[]>(() => {
    const subcategoryColors = new Map<string | symbol, EffectItemColor>()
    // Sort by subcategory before assigning colors to ensure consistency
    const sortedEffects = [...soundEffects.value].sort((a, b) => {
      // Put SUBCATEGORY_NONE at the start of the list
      if (a.subcategory === SUBCATEGORY_NONE) return -1
      if (b.subcategory === SUBCATEGORY_NONE) return 1
      return String(a.subcategory).localeCompare(String(b.subcategory))
    })
    return sortedEffects.map((effect) => {
      // Cycle through the colors for each subcategory
      const color = subcategoryColors.get(effect.subcategory) ?? SOUND_EFFECT_ITEM_COLORS[subcategoryColors.size]
      subcategoryColors.set(effect.subcategory, color)
      return { ...effect, color }
    })
  })

  const updateSoundEffects = (rawSoundEffects: WhiteboardSoundEffect[]): void => {
    soundEffects.value = rawSoundEffects.map((effect) => ({
      id: effect.id,
      title: effect.translated_name,
      category: 'sounds',
      subcategory: effect.translated_category,
      thumbnailUrl: effect.thumbnail_url,
      audioUrl: effect.audio_url,
      searchKeywords: effect.translated_search_keywords ?? '',
      trackingId: `${effect.category} - ${effect.name}`, // Use the English name for tracking
    }))
  }

  const fetchActiveSoundEffectsAndSaveToStore = async (): Promise<void> => {
    // Don't start fetching if we're already fetching or have fetched the full
    // list of sound effects.
    if (['fetching', 'success'].includes(soundEffectsFetchAllState.value)) return

    const ids = getAllSoundEffectIdsOnWhiteboard()
    const newIds = ids.filter((id) => !soundEffects.value.some((effect) => effect.id === id))
    if (newIds.length === 0) return

    try {
      soundEffectsFetchSubsetAbortController.value = new AbortController()
      const rawSoundEffects = await WhiteboardSoundEffectsApi.fetch(
        { ids: newIds },
        {
          signal: soundEffectsFetchSubsetAbortController.value.signal,
        },
      )

      // Check again after we get the response that we're not fetching or have fetched
      // the full list of sound effects.
      if (!['fetching', 'success'].includes(soundEffectsFetchAllState.value)) {
        updateSoundEffects(rawSoundEffects)
      }
    } catch (e) {
      captureFetchException(e, { source: 'fetchActiveSoundEffectsAndSaveToStore' })
    } finally {
      soundEffectsFetchSubsetAbortController.value = null
    }
  }

  const fetchAllAndSaveSoundEffectsToStore = async (): Promise<void> => {
    if (['fetching', 'success'].includes(soundEffectsFetchAllState.value)) return
    try {
      soundEffectsFetchAllState.value = 'fetching'

      // Cancel the subset fetch if it's running
      soundEffectsFetchSubsetAbortController.value?.abort()

      const rawSoundEffects = await WhiteboardSoundEffectsApi.fetch()
      updateSoundEffects(rawSoundEffects)
      soundEffectsFetchAllState.value = 'success'
    } catch (e) {
      captureFetchException(e, { source: 'fetchAllAndSaveSoundEffectsToStore' })
      soundEffectsFetchAllState.value = 'error'
    }
  }

  const getTrackingIdForSoundEffect = (soundEffectId: SoundEffectItem['id']): string | null => {
    return soundEffects.value.find((effect) => effect.id === soundEffectId)?.trackingId ?? null
  }
  // #endregion

  // #region Open/close special effects modal
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardAttachmentsStore = useWhiteboardAttachmentsStore()
  const xSpecialEffectsModal = ref(false)
  const selectedShapeId = ref<TLShapeId | null>(null)

  const canAddSpecialEffectForShape = (shape: TLShape): boolean => {
    const isFeatureFlagEnabled = isAppUsing('sandboxVisualFx') || isAppUsing('sandboxSoundFx')
    const isToolAllowed = whiteboardToolsStore.isToolEnabled(SPECIAL_EFFECTS_TOOL_ID)
    const isShapeTypeAllowed = ['draw', 'text', 'note', 'geo', 'padlet-speech-bubble'].includes(shape.type)
    const linkAttributes = whiteboardAttachmentsStore.getLinkAttributesForShape(shape.id)
    const isImageAttachment =
      shape.type === 'image' ||
      (shape.type === 'padlet-attachment-shape' && linkAttributes != null && LinkHelpers.isPhoto(linkAttributes))
    return (
      isFeatureFlagEnabled &&
      isToolAllowed &&
      (isShapeTypeAllowed || isImageAttachment || isGroupShape(shape)) &&
      !isShapeChildOfGroup(shape)
    )
  }

  const openSpecialEffectsModal = (): void => {
    const tldrawEditor = getEditorObject()
    const shapeId = tldrawEditor?.getOnlySelectedShapeId()
    if (shapeId == null) return
    selectedShapeId.value = shapeId
    xSpecialEffectsModal.value = true
  }

  const closeSpecialEffectsModal = (): void => {
    xSpecialEffectsModal.value = false
    selectedShapeId.value = null
  }
  // #endregion

  // #region CRUD for special effect in shape
  const getSpecialEffectsOfShape = (shapeId: TLShapeId): ShapeSpecialEffects => {
    if (shapeId == null) return {}

    const tldrawEditor = getEditorObject()
    if (tldrawEditor == null) return {}

    let shape = tldrawEditor.getShape(shapeId)
    if (shape == null) return {}

    // If shape is a child of a group, get the special effects on the parent group object
    if (isShapeChildOfGroup(shape)) {
      shape = tldrawEditor.getShape(shape.parentId)
    }

    if (shape == null) return {}

    return {
      visual_effect: shape.meta?.visual_effect as ShapeSpecialEffects['visual_effect'],
      sound_effect: shape.meta?.sound_effect as ShapeSpecialEffects['sound_effect'],
    }
  }

  const doesShapeHaveSpecialEffects = (shapeId: TLShapeId): boolean => {
    const specialEffects = getSpecialEffectsOfShape(shapeId)
    return Object.values(specialEffects).some((effect) => effect != null)
  }

  const specialEffectsInSelectedShape = ref<ShapeSpecialEffects>({})
  const lastUpdated = ref(Date.now())

  // Tldraw editor content is not reactive in Vue. In order to keep track of the
  // latest shape value, we need to trigger the effect manually. We do it by updating
  // the `lastUpdated` ref which is used inside the `unboundedWatchEffect` and thus
  // tracked by Vue.
  const triggerEffect = (): void => {
    lastUpdated.value = Date.now()
  }

  const noOp = (..._args: any[]): void => {}

  void unboundedWatchEffect(
    () => {
      if (selectedShapeId.value != null) {
        specialEffectsInSelectedShape.value = getSpecialEffectsOfShape(selectedShapeId.value)
      } else {
        specialEffectsInSelectedShape.value = {}
      }
      noOp(lastUpdated.value)
    },
    {
      // Run this watch effect synchronously when its dependencies change (ignore batching updates).
      // Since there aren't many reactive values involved, this should be fine to do. This allows us
      // to have the latest shape value when the modal is opened, for example.
      flush: 'sync',
    },
  )

  const updateSpecialEffectsInSelectedShape = ({
    soundEffect,
    visualEffect,
  }: {
    soundEffect: SoundEffectItem | null
    visualEffect: VisualEffectItem | null
  }): void => {
    if (selectedShapeId.value == null) return

    const tldrawEditor = getEditorObject()
    if (tldrawEditor == null) return

    const shape = tldrawEditor.getShape(selectedShapeId.value)
    if (shape == null) return

    const meta: ShapeSpecialEffects = {}
    const keysToDelete: string[] = []
    if (soundEffect != null) {
      meta.sound_effect = { type: 'system', effect_id: soundEffect.id }
    } else {
      keysToDelete.push('sound_effect')
    }
    if (visualEffect != null) {
      meta.visual_effect = { effect_id: visualEffect.id }
    } else {
      keysToDelete.push('visual_effect')
    }

    if (!isEmpty(meta)) {
      tldrawEditor.updateShape({
        id: shape.id,
        type: shape.type,
        meta: meta as Partial<JsonObject>,
      })

      // If the shape is a group, propagate the special effects to all its child shapes
      // NOTE: For now, we don't want to delete the special effects on child shapes when the parent group's special effects are deleted
      // Otherwise, move it to outside of this `if (!isEmpty(meta))` statement, to propagate the deletion to child shapes
      if (tldrawEditor.isShapeOfType(shape, 'group')) {
        updateSpecialEffectsInAllShapesInGroup(shape.id, {
          soundEffectId: soundEffect?.id ?? null,
          visualEffectId: visualEffect?.id ?? null,
        })
      }

      if (meta.visual_effect != null) {
        trackEvent('Whiteboard', 'Set visual effect', meta.visual_effect.effect_id, undefined, {
          shapeType: shape.type,
        })
      }
      if (meta.sound_effect != null) {
        trackEvent(
          'Whiteboard',
          'Set sound effect',
          getTrackingIdForSoundEffect(meta.sound_effect.effect_id),
          undefined,
          {
            shapeType: shape.type,
          },
        )
      }
    }

    if (keysToDelete.length > 0) {
      deleteShapeMetadata(selectedShapeId.value, keysToDelete)
    }

    triggerEffect()
  }
  // #endregion

  // #region Trigger special effects
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const currentUser = computed(() => surfaceCurrentUserStore.currentUser)
  const { isInReducedMotionMode } = useReducedMotionMode(currentUser)
  const previousReducedMotionSnackbarId = ref<string | null>(null)

  const playVisualEffect = async (effectId: VisualEffectItem['id'], shapeId: TLShapeId): Promise<void> => {
    vfxManager.stopAllEffects()
    const stats = await vfxManager.executeEffect(effectId, { origin: getShapeOriginInViewport(shapeId) })
    if (stats != null) {
      trackEvent('Whiteboard', 'Visual effect stats', effectId, undefined, {
        averageFps: stats.averageFps,
        minFps: stats.minFps,
        maxFps: stats.maxFps,
        cpuCores: window.navigator?.hardwareConcurrency,
        deviceMemory: (window.navigator as any)?.deviceMemory,
      })
    }
  }

  const triggerSpecialEffectForShape = (shapeId: TLShapeId): void => {
    const { visual_effect: visualEffect, sound_effect: soundEffect } = getSpecialEffectsOfShape(shapeId)
    if (isAppUsing('sandboxVisualFx') && visualEffect != null) {
      if (isInReducedMotionMode.value) {
        if (previousReducedMotionSnackbarId.value != null) {
          globalSnackbarStore.removeSnackbar(previousReducedMotionSnackbarId.value)
        }
        previousReducedMotionSnackbarId.value = globalSnackbarStore.setSnackbar({
          message: __('Visual effect is not played in reduced motion mode.'),
          timeout: 5000,
          actionText: __('Play anyway'),
          actionTextActions: [
            () => {
              void vfxManager.executeEffect(visualEffect.effect_id, {
                origin: getShapeOriginInViewport(shapeId),
              })
              if (previousReducedMotionSnackbarId.value != null) {
                globalSnackbarStore.removeSnackbar(previousReducedMotionSnackbarId.value)
              }
            },
          ],
        })
      } else {
        void playVisualEffect(visualEffect.effect_id, shapeId)
      }
      trackEvent('Whiteboard', 'Triggered visual effect', visualEffect.effect_id)
    }
    if (isAppUsing('sandboxSoundFx') && soundEffect != null) {
      const audioUrl = soundEffects.value.find((effect) => effect.id === soundEffect.effect_id)?.audioUrl
      if (audioUrl != null) {
        void sfxManager.executeEffect(audioUrl)
      }
      trackEvent('Whiteboard', 'Triggered sound effect', getTrackingIdForSoundEffect(soundEffect.effect_id))
    }
  }
  // #endregion

  // #region Preload active special effects
  const whiteboardModeStore = useWhiteboardModeStore()

  const fetchActiveSoundEffectsAndPreload = async (): Promise<void> => {
    await fetchActiveSoundEffectsAndSaveToStore()
    await sfxManager.preloadEffects(soundEffects.value.map((effect) => effect.audioUrl))
  }

  const preloadSpecialEffectsWhenEnterPlayMode = (): void => {
    const stopWatcherToFetchSfx = watch(
      () => whiteboardModeStore.isPlayMode,
      async (isPlayMode) => {
        if (isAppUsing('sandboxSoundFx') && isPlayMode) {
          await fetchActiveSoundEffectsAndPreload()
          setTimeout(() => {
            stopWatcherToFetchSfx()
          }, 0)
        }
      },
      { immediate: true },
    )

    const stopWatcherToPreloadVfx = watch(
      () => whiteboardModeStore.isPlayMode,
      (isPlayMode) => {
        if (isAppUsing('sandboxVisualFx') && isPlayMode) {
          void preloadActiveVisualEffects()
          setTimeout(() => {
            stopWatcherToPreloadVfx()
          }, 0)
        }
      },
      { immediate: true },
    )
  }
  // #endregion

  // #region Grouping shapes

  // Store the special effects for the most recently created group
  // This is used to temporarily hold the special effects that should be applied to a new group
  const activeGroupSpecialEffectIds = ref<{
    soundEffectId: string | null
    visualEffectId: string | null
  }>({
    soundEffectId: null,
    visualEffectId: null,
  })

  const setActiveGroupSpecialEffects = (specialEffects: {
    soundEffectId?: string | null
    visualEffectId?: string | null
  }): void => {
    activeGroupSpecialEffectIds.value = { ...activeGroupSpecialEffectIds.value, ...specialEffects }
  }

  const resetActiveGroupSpecialEffects = (): void => {
    activeGroupSpecialEffectIds.value = {
      soundEffectId: null,
      visualEffectId: null,
    }
  }
  // #endregion

  return {
    visualEffects,
    allVisualEffectIds,

    soundEffectsFetchAllState,
    soundEffectsWithColor,
    fetchAllAndSaveSoundEffectsToStore,

    xSpecialEffectsModal: readonly(xSpecialEffectsModal),
    canAddSpecialEffectForShape,
    openSpecialEffectsModal,
    closeSpecialEffectsModal,

    specialEffectsInSelectedShape,
    doesShapeHaveSpecialEffects,
    updateSpecialEffectsInSelectedShape,

    triggerSpecialEffectForShape,

    fetchActiveSoundEffectsAndPreload,
    preloadActiveVisualEffects,
    preloadSpecialEffectsWhenEnterPlayMode,

    activeGroupSpecialEffectIds,
    setActiveGroupSpecialEffects,
    resetActiveGroupSpecialEffects,
  }
})

export { CATEGORY_ID_TO_SHAPE_SPECIAL_EFFECT_KEY_MAP, SOUND_EFFECT_ITEM_COLORS, SUBCATEGORY_NONE }
export type {
  EffectItemColor,
  ShapeSpecialEffects,
  SoundEffectItem,
  SoundEffectItemWithColor,
  SpecialEffectCategory,
  SpecialEffectItem,
  VisualEffectItem,
}
